import { supabase } from '@/lib/supabase';
import { lastOccurrence, type RitualOccurrence, type RitualRhythm, type WorkspaceRitual } from '@shared/types/workspace-rituals';

/** Today in the person's own calendar (YYYY-MM-DD). */
export const localToday = (now: Date = new Date()): string =>
  `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

export interface DueRitual {
  ritual: WorkspaceRitual;
  /** The occurrence shown: today, or an earlier one not done yet. */
  occursOn: string;
  /** The occurrence as declared, once done. */
  occurrence: RitualOccurrence | null;
}

/** My active rituals with their current occurrence, done or not; those with none yet are left out. */
export const loadDueRituals = async (userId: string, today: string = localToday()): Promise<DueRitual[]> => {
  const { data, error } = await supabase.from('workspace_rituals').select('*')
    .eq('owner_user_id', userId).eq('active', true).order('created_at');
  if (error) throw new Error('Les rituels ne sont pas accessibles pour le moment.');
  const rituals = (data ?? []) as WorkspaceRitual[];
  const due = rituals.flatMap((ritual) => {
    const occursOn = lastOccurrence(ritual, today);
    return occursOn ? [{ ritual, occursOn }] : [];
  });
  if (due.length === 0) return [];
  const { data: done, error: doneError } = await supabase.from('workspace_ritual_occurrences').select('*')
    .in('ritual_id', due.map(({ ritual }) => ritual.id)).in('occurs_on', [...new Set(due.map(({ occursOn }) => occursOn))]);
  if (doneError) throw new Error('Les rituels ne sont pas accessibles pour le moment.');
  const occurrences = (done ?? []) as RitualOccurrence[];
  return due.map(({ ritual, occursOn }) => ({
    ritual, occursOn,
    occurrence: occurrences.find((row) => row.ritual_id === ritual.id && row.occurs_on === occursOn) ?? null,
  }));
};

/** Done for this time, with what the person noted and decided (both optional); saved again to change them. */
export const saveRitualOccurrence = async (
  ritualId: string, occursOn: string, declared: { note: string | null; decision: string | null },
): Promise<RitualOccurrence> => {
  const { data, error } = await supabase.from('workspace_ritual_occurrences')
    .upsert({ ritual_id: ritualId, occurs_on: occursOn, note: declared.note?.trim() || null, decision: declared.decision?.trim() || null }, { onConflict: 'ritual_id,occurs_on' })
    .select('*').single();
  if (error || !data) throw new Error('Le rituel n’a pas été enregistré. Réessayez.');
  return data as RitualOccurrence;
};

export const undoRitualOccurrence = async (ritualId: string, occursOn: string): Promise<void> => {
  const { error } = await supabase.from('workspace_ritual_occurrences').delete().eq('ritual_id', ritualId).eq('occurs_on', occursOn);
  if (error) throw new Error('Le rituel n’a pas été mis à jour. Réessayez.');
};

/** The earlier times of a ritual, most recent first. */
export const loadRitualHistory = async (ritualId: string, before: string): Promise<RitualOccurrence[]> => {
  const { data, error } = await supabase.from('workspace_ritual_occurrences').select('*')
    .eq('ritual_id', ritualId).lt('occurs_on', before).order('occurs_on', { ascending: false });
  if (error) throw new Error('L’historique n’est pas accessible pour le moment.');
  return (data ?? []) as RitualOccurrence[];
};

export interface RitualInput {
  ownerUserId: string;
  title: string;
  detail: string | null;
  rhythm: RitualRhythm;
  days: number[];
  route: string | null;
  sourceUsageId: string | null;
}

export const createRitual = async (input: RitualInput): Promise<WorkspaceRitual> => {
  const { data, error } = await supabase.from('workspace_rituals').insert({
    owner_user_id: input.ownerUserId,
    title: input.title,
    detail: input.detail,
    rhythm: input.rhythm,
    days: input.days,
    route: input.route,
    source_usage_id: input.sourceUsageId,
  }).select('*').single();
  if (error || !data) throw new Error('Le rituel n’a pas été ajouté. Vérifiez vos droits et réessayez.');
  return data as WorkspaceRitual;
};

/** The rituals created from the qualification's proposals, by proposal id (active or stopped). */
export const loadRitualsBySource = async (): Promise<Record<string, WorkspaceRitual>> => {
  const { data, error } = await supabase.from('workspace_rituals').select('*').not('source_usage_id', 'is', null).order('created_at');
  if (error) throw new Error('Les rituels ne sont pas accessibles pour le moment.');
  return Object.fromEntries(((data ?? []) as WorkspaceRitual[]).map((ritual) => [ritual.source_usage_id!, ritual]));
};

export const setRitualActive = async (ritualId: string, active: boolean): Promise<void> => {
  const { error } = await supabase.from('workspace_rituals').update({ active }).eq('id', ritualId);
  if (error) throw new Error('Le rituel n’a pas été mis à jour. Réessayez.');
};

/** Where « Faire » leads (an app, the team's week, the review of the apps, a page). */
export const updateRitualRoute = async (ritualId: string, route: string | null): Promise<WorkspaceRitual> => {
  const { data, error } = await supabase.from('workspace_rituals').update({ route }).eq('id', ritualId).select('*').single();
  if (error || !data) throw new Error('Le rituel n’a pas été mis à jour. Réessayez.');
  return data as WorkspaceRitual;
};

/** The apps a ritual can open: those with a list, as the person can read them. */
export const loadRitualApps = async (): Promise<Array<{ key: string; title: string }>> => {
  const { data, error } = await supabase.from('app_collections').select('app:tool_apps(key,title)');
  if (error) throw new Error('Les applications ne sont pas accessibles pour le moment.');
  const apps = ((data ?? []) as unknown as Array<{ app: { key: string; title: string } | null }>).flatMap(({ app }) => (app ? [app] : []));
  return [...new Map(apps.map((app) => [app.key, app])).values()].sort((left, right) => left.title.localeCompare(right.title, 'fr'));
};
